import React, { useEffect, useState } from "react";

import CloseIcon from "@material-ui/icons/Close";
import IconButton from "@material-ui/core/IconButton";
import { Card, CardContent, CircularProgress, Divider, Grid, makeStyles, Paper, Toolbar, Typography } from "@material-ui/core";
import { AssetCategory } from "../../../../entities/AssetCategory";
import { BuilderAssetService } from "../../../../services/BuilderAssetService";
import { SermonTemplateService } from "../../../../services/SermonTemplateService";
import { ServerFailed } from "../../../errors/serverFailed";
import { BuilderAsset } from "../../../../entities/BuilderAsset";
import { SermonTemplate } from "../../../../entities/SermonTemplate";

const useStyles = makeStyles(() => ({
	card: {
		minHeight: 385,
		opacity: 1,
		transition: "all 0.3s ease 0s",
		MozTransition: "all 0.3s ease 0s",
		WebkitTransition: "all 0.3s ease 0s",
		cursor: "pointer",
		"&:hover": {
			opacity: 0.9,
			transition: "all 0.3s ease 0s",
			MozTransition: "all 0.3s ease 0s",
			WebkitTransition: "all 0.3s ease 0s",
		},
	},
}));

interface Props {
	category: AssetCategory;
	onClose: () => void;
	onSermonTemplateSelected: (sermonTemplate: SermonTemplate) => void;
	onCustomTemplateSelected: (customTemplate: BuilderAsset) => void;
}

export function SelectTemplate(props: Props) {
	const { category } = props;

	const [templates, setTemplates] = useState<{ customTemplates: BuilderAsset[]; sermonTemplates: SermonTemplate[] } | undefined>(undefined);
    const [serverError, setServerError] = useState(false);

	useEffect(() => {
		async function loadTemplates() {
			const responses = await Promise.all([SermonTemplateService.getAllEnabled(), BuilderAssetService.getTemplates()]);

			const [sermonTemplateResponse, customTemplateResponse] = responses;

			if (sermonTemplateResponse.success && customTemplateResponse.success) {
                setTemplates({ sermonTemplates: sermonTemplateResponse.data, customTemplates: customTemplateResponse.data});
			} else {
                setServerError(true);
            }
		}

        loadTemplates();
	}, []);	

	return (
		<Paper style={{ overflowX: "hidden", overflowY: "auto", height: "100%" }} elevation={2}>
			<Toolbar>
				<IconButton color="inherit" onClick={props.onClose} aria-label="Close">
					<CloseIcon />
				</IconButton>
				<Typography variant="h6" color="inherit" align="center">
					Choose a template
				</Typography>
			</Toolbar>
			<Divider />
			{
				serverError
				&&
				<ServerFailed errorMessage="Could not load Templates" />
			}
			{templates ? (
				<TemplateList
					category={category}
					templates={templates}
					onCustomTemplateSelected={props.onCustomTemplateSelected}
					onSermonTemplateSelected={props.onSermonTemplateSelected}
				/>
			) : (
				<div style={{ textAlign: "center", width: "100%", marginTop: "25%" }}>
					<CircularProgress style={{ marginTop: "50px" }} />
					<br />
					<Typography variant="body1" color="textSecondary" style={{ textAlign: "center" }}>
						Loading Templates...
					</Typography>
				</div>
			)}
		</Paper>
	);
}

interface TemplateListProps {
	templates: { customTemplates: BuilderAsset[]; sermonTemplates: SermonTemplate[] };
	category: AssetCategory;
	onSermonTemplateSelected: (sermonTemplate: SermonTemplate) => void;
	onCustomTemplateSelected: (customTemplate: BuilderAsset) => void;
}

function TemplateList(props: TemplateListProps) {
	const { templates, category } = props;
	const classes = useStyles();
	return (
		<Grid container spacing={3} style={{ padding: "20px" }}>
			{templates.customTemplates
				.filter((a) => a.category === category)
				.map((customTemplate) => {
					return (
						<Grid item key={customTemplate.id} xs={12} sm={3}>
							<Card className={classes.card} onClick={() => props.onCustomTemplateSelected(customTemplate)}>
								<CardContent>
									<Typography gutterBottom variant="h5" component="h2">
										{customTemplate.name}
									</Typography>
									<Typography variant="body2" color="textSecondary" component="p">
										{customTemplate.templateDescription}
									</Typography>
								</CardContent>
							</Card>
						</Grid>
					);
				})}
			{templates.sermonTemplates
				.filter((a) => a.category.includes(category))
				.map((sermonTemplate) => {
					return (
						<Grid item key={sermonTemplate.id} xs={12} sm={3}>
							<Card
								id={sermonTemplate.id.toString()}
								className={classes.card}
								onClick={() => props.onSermonTemplateSelected(sermonTemplate)}
							>
								<img alt="Sermon Template" width={"100%"} src={sermonTemplate.thumbnailUrl} />
								<CardContent>
									<Typography gutterBottom variant="h5" component="h2">
										{sermonTemplate.name}
									</Typography>
									<Typography variant="body2" color="textSecondary" component="p">
										{sermonTemplate.description}
									</Typography>
								</CardContent>
							</Card>
						</Grid>
					);
				})}
		</Grid>
	);
}